import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../auth/useAuthStore';
import westeerLogo from '../auth/westeer-logo-white.png';
import './no-projects-available.css';

/**
 * NoProjectsAvailable - Componente que se muestra cuando el usuario no tiene proyectos asignados
 *
 * @remarks
 * Se muestra después del login cuando el usuario no tiene acceso a ningún proyecto.
 * Proporciona mensaje claro y opciones para logout o contactar al administrador.
 *
 * @example
 * ```tsx
 * import { NoProjectsAvailable } from '@/core/projects';
 *
 * function App() {
 *   const hasProjects = checkUserProjects();
 *
 *   if (!hasProjects) {
 *     return <NoProjectsAvailable />;
 *   }
 *
 *   return <MainApp />;
 * }
 * ```
 */
export function NoProjectsAvailable(): JSX.Element {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();

  /**
   * Maneja el logout del usuario
   */
  const handleLogout = (): void => {
    logout();
    navigate('/login');
  };

  return (
    <div className="no-projects-page">
      {/* Background oscuro */}
      <div className="no-projects-overlay"></div>

      {/* Contenido centrado */}
      <div className="no-projects-content">
        {/* Logo */}
        <div className="no-projects-logo">
          <img
            src={westeerLogo}
            alt="Wayline"
            className="no-projects-logo-img"
          />
        </div>

        {/* Card con mensaje */}
        <div className="no-projects-card">
          <div className="no-projects-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1 className="no-projects-title">
            {t('noProjects.title')}
          </h1>

          <p className="no-projects-description">
            {t('noProjects.description')}
          </p>

          {/* Info del usuario */}
          {user && (
            <div className="no-projects-user-info">
              <div className="no-projects-user-label">
                {t('noProjects.currentUser')}
              </div>
              <div className="no-projects-user-email">
                {user.email}
              </div>
              <div className="no-projects-user-tenant">
                {user.tenant?.name || t('noProjects.noTenant')}
              </div>
            </div>
          )}

          {/* Instrucciones */}
          <div className="no-projects-instructions">
            <p>{t('noProjects.instructions')}</p>
            <ul>
              <li>{t('noProjects.step1')}</li>
              <li>{t('noProjects.step2')}</li>
              <li>{t('noProjects.step3')}</li>
            </ul>
          </div>

          {/* Botones de acción */}
          <div className="no-projects-actions">
            <button
              onClick={handleLogout}
              className="no-projects-btn no-projects-btn-primary"
            >
              {t('noProjects.logout')}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="no-projects-footer">
          <p>{t('noProjects.support')}</p>
        </div>
      </div>
    </div>
  );
}
